import { SHeaderSortable, STableContainer } from "@avalara/skylab-sdk/react";
import classnames from "classnames";
import React from "react";
import NoData from "../../features/search/NoData";
import RecordCount from "../../features/search/RecordCount";
import { DataGridProps, GridRowId } from "./types";

// Type declarations for legacy JSX components
const TypedRecordCount = RecordCount as React.ComponentType<{
    recordCount: number;
    recordName: string;
    selectedCount: number;
    hasFilterChips?: boolean;
    usesFilterChips?: boolean;
}>;

const TypedNoData = NoData as React.ComponentType<{
    showGenNoDataMsg?: boolean;
    errorMessage?: string;
}>;

function DataGrid<T extends { id: number | string }>({
    gridHeaders,
    gridData,
    sortFunction,
    gridName,
    totalRecords,
    sortColumn,
    hasFilterChips = false,
    usesFilterChips = false,
    scrollable = false,
    scrollableClassName = "",
    onScroll,
    errorMessage,
    testId,
    selectable = false,
    selectedIds,
    onChangeSelection,
    isRowSelectable,
    rowSelectionTitle,
    rowSelectionLabel,
}: DataGridProps<T>): React.JSX.Element {
    // Show error state if errorMessage is provided
    if (errorMessage) {
        return <TypedNoData showGenNoDataMsg={false} errorMessage={errorMessage} />;
    }

    // Show empty state if no data
    if (!gridData || gridData.length === 0) {
        return <TypedNoData showGenNoDataMsg />;
    }

    const selection = new Set<GridRowId>(selectedIds ?? []);
    const canSelectRow = (row: T): boolean => (isRowSelectable ? isRowSelectable(row) : true);
    // Select-all spans only what this page can actually offer: the eligible rows currently
    // rendered. Ineligible rows are never handed to the caller, and rows on other pages are
    // not implied — the grid cannot see them.
    const selectableRows = gridData.filter(canSelectRow);
    const allSelected =
        selectableRows.length > 0 && selectableRows.every(row => selection.has(row.id));
    const someSelected = !allSelected && selectableRows.some(row => selection.has(row.id));

    const emit = (ids: Set<GridRowId>): void => onChangeSelection?.([...ids]);

    const toggleRow = (row: T): void => {
        const next = new Set(selection);
        if (next.has(row.id)) {
            next.delete(row.id);
        } else {
            next.add(row.id);
        }
        emit(next);
    };

    const toggleAll = (): void => {
        const next = new Set(selection);
        // Only this page's eligible rows are added or removed, so a selection made on an
        // earlier page survives a select-all here rather than being silently dropped.
        for (const row of selectableRows) {
            if (allSelected) {
                next.delete(row.id);
            } else {
                next.add(row.id);
            }
        }
        emit(next);
    };

    const tableClass = classnames({
        "scrollable-grid": scrollable,
        "top-negative-20": hasFilterChips,
        [scrollableClassName]: !!scrollableClassName,
    });

    const tableContent = (
        <STableContainer>
            <table>
                <thead>
                    <tr>
                        {selectable ? (
                            <th className="dg-checkbox">
                                <input
                                    type="checkbox"
                                    className="margin-right-sm unlabeled"
                                    aria-label="Select All"
                                    checked={allSelected}
                                    ref={node => {
                                        // Indeterminate is DOM-only — React has no prop for it.
                                        if (node) {
                                            node.indeterminate = someSelected;
                                        }
                                    }}
                                    disabled={selectableRows.length === 0}
                                    onChange={toggleAll}
                                />
                            </th>
                        ) : null}
                        {gridHeaders?.map(header => (
                            <th key={header.id}>
                                {header.sortable ? (
                                    <SHeaderSortable
                                        text={header.label || ""}
                                        ascending={sortColumn[1]}
                                        descending={sortColumn[0] === header.id && !sortColumn[1]}
                                        active={sortColumn[0] === header.id}
                                        onS-sort={() => sortFunction(header.id)}
                                    />
                                ) : (
                                    header.label || ""
                                )}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {gridData?.map(rowData => (
                        <tr key={rowData.id}>
                            {selectable ? (
                                <td className="dg-checkbox">
                                    <input
                                        type="checkbox"
                                        className="margin-right-sm unlabeled"
                                        aria-label={
                                            rowSelectionLabel?.(rowData) ??
                                            `Select row ${rowData.id}`
                                        }
                                        title={rowSelectionTitle?.(rowData)}
                                        checked={selection.has(rowData.id)}
                                        disabled={!canSelectRow(rowData)}
                                        onChange={() => toggleRow(rowData)}
                                    />
                                </td>
                            ) : null}
                            {gridHeaders.map(header => (
                                <td key={header.id}>
                                    {header.render
                                        ? (header.render(rowData) as React.ReactNode)
                                        : String(
                                              (rowData as Record<string, unknown>)[header.id] ?? ""
                                          )}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </STableContainer>
    );

    return (
        <>
            <TypedRecordCount
                recordCount={totalRecords}
                recordName={gridName}
                selectedCount={selection.size}
                hasFilterChips={hasFilterChips}
                usesFilterChips={usesFilterChips}
            />
            {scrollable ? (
                <div className={tableClass} data-testid={testId} onScroll={onScroll}>
                    {tableContent}
                </div>
            ) : (
                <div data-testid={testId}>{tableContent}</div>
            )}
        </>
    );
}

export default DataGrid;
